import { Component, inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TherapistApiService } from '@therapist/shared/services/api/therapist-api.service';
import { TherapistAuthStateService } from '@therapist/shared/services/state/therapist-auth-state.service';
import { TherapistRegisterDto } from '@therapist/shared/models/therapist/therapist-register.dto';
import { CountryEnum } from '@base/models/country.enum';
import { NgTemplateOutlet } from '@angular/common';

/**
 * Mindestens 8 Zeichen, davon mindestens ein Großbuchstabe, ein Kleinbuchstabe und eine Zahl.
 */
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

const passwordsMatchValidator = (group: AbstractControl): ValidationErrors | null => {
  const password = group.get('password')?.value;
  const passwordConfirmation = group.get('passwordConfirmation')?.value;

  if (password && passwordConfirmation && password !== passwordConfirmation) {
    return { passwordMismatch: true };
  }

  return null;
};

const addressAllOrNothingValidator = (group: AbstractControl): ValidationErrors | null => {
  const values = Object.values((group as FormGroup).controls).map(control => control.value);

  const isFilled = (value: unknown) => value !== null && value !== undefined && `${value}`.trim().length > 0;

  const anyFilled = values.some(isFilled);
  const allFilled = values.every(isFilled);

  if (anyFilled && !allFilled) {
    return { addressIncomplete: true };
  }

  return null;
};

@Component({
  selector: 'sana-tp-register-page',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    NgTemplateOutlet
  ],
  templateUrl: './register-page.component.html',
  styleUrl: './register-page.component.scss'
})
export class RegisterPageComponent implements OnInit {

  readonly #fb = inject(FormBuilder);
  readonly #authStateService = inject(TherapistAuthStateService);
  readonly #therapistApiService = inject(TherapistApiService);
  readonly #router = inject(Router);

  readonly registerForm = this.#fb.group({
    titlePrefix: new FormControl<string | null>(null, [Validators.maxLength(255)]),
    firstName: new FormControl<string | null>(null, [
      Validators.required,
      Validators.maxLength(255)
    ]),
    lastName: new FormControl<string | null>(null, [
      Validators.required,
      Validators.maxLength(255)
    ]),
    titleSuffix: new FormControl<string | null>(null, [Validators.maxLength(255)]),
    email: new FormControl<string | null>(null, [
      Validators.required,
      Validators.email,
      Validators.maxLength(255)
    ]),
    password: new FormControl<string | null>(null, [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(255),
      Validators.pattern(passwordRegex)
    ]),
    passwordConfirmation: new FormControl<string | null>(null, [Validators.required]),
    phoneNumber: new FormControl<string | null>(null, [Validators.maxLength(255)]),
    fieldOfSpecialisation: new FormControl<string | null>(null, [Validators.maxLength(255)]),
    instituteName: new FormControl<string | null>(null, [
      Validators.required,
      Validators.maxLength(255)
    ]),
    address: this.#fb.group({
      street: new FormControl<string | null>(null, [Validators.maxLength(255)]),
      houseNumber: new FormControl<string | null>(null, [Validators.maxLength(255)]),
      zipCode: new FormControl<string | null>(null, [Validators.maxLength(255)]),
      city: new FormControl<string | null>(null, [Validators.maxLength(255)]),
      country: new FormControl<CountryEnum | null>(null)
    }, { validators: [addressAllOrNothingValidator] })
  }, { validators: [passwordsMatchValidator] });

  readonly countryOptions = [CountryEnum.Austria, CountryEnum.Germany, CountryEnum.Switzerland];

  readonly steps = [RegisterStepEnum.Personal, RegisterStepEnum.Practice, RegisterStepEnum.Password];
  readonly stepLabels = ['Deine Daten', 'Optionale Angaben', 'Passwort'];

  // Rechtsseiten existieren auf sanactivo.com noch nicht, bis dahin zeigen beide Links auf die Startseite
  readonly termsUrl = 'https://sanactivo.com/';
  readonly privacyUrl = 'https://sanactivo.com/';

  formStep = signal(RegisterStepEnum.Personal);
  isSubmitting = signal(false);
  isRegistered = signal(false);
  errorMessage = signal<string | null>(null);
  showPassword = signal(false);
  showPasswordConfirmation = signal(false);

  ngOnInit() {
    if (this.#authStateService.hasValidToken()) {
      this.#router.navigate(['/']);
    }
  }

  getCountryTranslation(country: CountryEnum): string {
    switch (country) {
      case CountryEnum.Austria:
        return 'Österreich';
      case CountryEnum.Germany:
        return 'Deutschland';
      case CountryEnum.Switzerland:
        return 'Schweiz';
    }
  }

  togglePassword(): void {
    this.showPassword.update(value => !value);
  }

  togglePasswordConfirmation(): void {
    this.showPasswordConfirmation.update(value => !value);
  }

  routeToLogin(): void {
    this.#router.navigate(['/login']);
  }

  nextStep(): void {
    this.errorMessage.set(null);

    const stepControls = this.getCurrentStepControls();
    stepControls.forEach(control => control.markAllAsTouched());

    if (stepControls.some(control => control.invalid)) {
      return;
    }

    this.formStep.set(
      this.formStep() === RegisterStepEnum.Personal
        ? RegisterStepEnum.Practice
        : RegisterStepEnum.Password
    );
  }

  previousStep(): void {
    this.formStep.set(
      this.formStep() === RegisterStepEnum.Password
        ? RegisterStepEnum.Practice
        : RegisterStepEnum.Personal
    );
  }

  onSubmit(): void {
    if (this.formStep() !== RegisterStepEnum.Password) {
      this.nextStep();
      return;
    }

    this.registerForm.markAllAsTouched();
    this.errorMessage.set(null);

    if (this.registerForm.invalid || this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);

    this.#therapistApiService
      .register(this.buildPayload())
      .subscribe({
        next: () => {
          this.isRegistered.set(true);
          this.isSubmitting.set(false);
        },
        error: (error: HttpErrorResponse) => this.handleRegisterError(error)
      });
  }

  private getCurrentStepControls(): AbstractControl[] {
    const ctrls = this.registerForm.controls;

    if (this.formStep() === RegisterStepEnum.Personal) {
      return [ctrls.firstName, ctrls.lastName, ctrls.email, ctrls.instituteName];
    }

    return [ctrls.titlePrefix, ctrls.titleSuffix, ctrls.fieldOfSpecialisation, ctrls.phoneNumber, ctrls.address];
  }

  private buildPayload(): TherapistRegisterDto {
    const ctrls = this.registerForm.controls;

    const payload: TherapistRegisterDto = {
      firstName: ctrls.firstName.value!,
      lastName: ctrls.lastName.value!,
      email: ctrls.email.value!,
      password: ctrls.password.value!,
      instituteName: ctrls.instituteName.value!
    };

    if (this.isFilled(ctrls.titlePrefix.value)) {
      payload.titlePrefix = ctrls.titlePrefix.value!;
    }

    if (this.isFilled(ctrls.titleSuffix.value)) {
      payload.titleSuffix = ctrls.titleSuffix.value!;
    }

    if (this.isFilled(ctrls.phoneNumber.value)) {
      payload.phoneNumber = ctrls.phoneNumber.value!;
    }

    if (this.isFilled(ctrls.fieldOfSpecialisation.value)) {
      payload.fieldOfSpecialisation = ctrls.fieldOfSpecialisation.value!;
    }

    const addressCtrls = ctrls.address.controls;

    if (this.isFilled(addressCtrls.street.value)) {
      payload.address = {
        street: addressCtrls.street.value!,
        houseNumber: addressCtrls.houseNumber.value!,
        zipCode: addressCtrls.zipCode.value!,
        city: addressCtrls.city.value!,
        country: addressCtrls.country.value!
      };
    }

    return payload;
  }

  private isFilled(value: string | null): boolean {
    return value !== null && value !== undefined && value.trim().length > 0;
  }

  private handleRegisterError(error: HttpErrorResponse): void {
    if (error.status === 409) {
      this.errorMessage.set('Diese E-Mail-Adresse ist bereits registriert.');
    } else if (error.status === 429) {
      this.errorMessage.set('Zu viele Registrierungsversuche. Bitte versuche es später erneut.');
    } else {
      this.errorMessage.set('Registrierung fehlgeschlagen. Bitte versuche es später erneut.');
    }

    this.isSubmitting.set(false);
  }

  protected readonly RegisterStepEnum = RegisterStepEnum;
}

export enum RegisterStepEnum {
  Personal,
  Practice,
  Password
}
